import { getStoredToken } from './authApi';
import { getServerUrl, getServerWsUrl } from './updateChannel';
import { ensureClientVersion, reportWebSocketRejection } from './versionCheck';
import { isWeb } from './platform';

// Set by the server on close-code 4001 to short-circuit the reconnect
// loop while the player is on the forced-update modal.
let versionRejected = false;
export function isVersionRejected() { return versionRejected; }
export function clearVersionRejection() { versionRejected = false; }

// Set when the server retires this connection because the same account
// connected somewhere else (session:superseded message and/or close-code
// 4002). Latches the reconnect loop off: reconnecting from here would
// kick the other client, whose own auto-reconnect would kick us back,
// and the two would fight at machine speed forever (qa#124). The player
// re-enters deliberately via reclaimSession(), or the latch resets on an
// explicit local disconnect (logout).
let sessionSuperseded = false;
let supersededInfo = null;
const supersededListeners = new Set();
export function isSessionSuperseded() { return sessionSuperseded; }
export function getSupersededInfo() { return supersededInfo; }

// Subscribe to supersede events. Fires immediately on subscribe if the
// latch is already set, so a component mounting after the event still
// learns about it.
export function onSessionSuperseded(callback) {
  supersededListeners.add(callback);
  if (sessionSuperseded) {
    try { callback(supersededInfo || {}); } catch (err) { console.error('[serverClient] superseded listener error:', err); }
  }
  return () => supersededListeners.delete(callback);
}

function markSuperseded(info) {
  if (sessionSuperseded) return;
  sessionSuperseded = true;
  supersededInfo = info || {};
  for (const cb of supersededListeners) {
    try { cb(supersededInfo); } catch (err) { console.error('[serverClient] superseded listener error:', err); }
  }
}

// The player chose to play here again. Clears the latch and reconnects,
// which supersedes whichever client took over. Resolves like
// connectWebSocket: true once the socket is open.
export function reclaimSession() {
  sessionSuperseded = false;
  supersededInfo = null;
  return connectWebSocket();
}

// SERVER_URL / WS_URL resolve per-call so a stable ↔ beta switch takes
// effect without a full renderer reload. Exporting them as getter
// functions (not bare constants) keeps every caller honest. A cached
// `const url = SERVER_URL` would otherwise pin a caller to whatever
// channel was active at import time.
export const SERVER_URL = () => getServerUrl();
export const WS_URL = () => getServerWsUrl();

// --- REST helper ---

// Thrown for every non-2xx API response. `status` lets callers tell a
// permanent rejection (4xx. Re-sending the same payload can never
// succeed) from a transient fault (5xx; pure network failures throw a
// TypeError with no status at all). Retry queues key their
// drop-vs-retry decision off it. See logUpload.uploadMatchLog.
export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function authHeaders() {
  const token = await getStoredToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Notified once when an authenticated REST call returns 401. The app
// subscribes to route the user back to the login screen. Guarded by
// `sessionExpiredFired` so a burst of in-flight requests after a token
// is revoked produces a single logout, not N. The flag resets on the
// next successful (2xx) response, so a fresh sign-in re-arms it.
const sessionExpiredListeners = new Set();
let sessionExpiredFired = false;

export function onSessionExpired(callback) {
  sessionExpiredListeners.add(callback);
  return () => sessionExpiredListeners.delete(callback);
}

// Paths whose 401s are NOT session-expiry. E.g. /auth/verify returns
// 401 on a wrong magic code, which is a normal login-screen interaction
// and must not cascade into a logout.
function isAuthPath(path) {
  return path.startsWith('/auth/');
}

async function request(method, path, body) {
  const headers = await authHeaders();
  const init = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);

  const res = await fetch(`${getServerUrl()}${path}`, init);

  if (res.status === 401 && !isAuthPath(path) && headers.Authorization) {
    if (!sessionExpiredFired) {
      sessionExpiredFired = true;
      for (const cb of sessionExpiredListeners) {
        try { cb(); } catch (err) { console.error('[serverClient] session-expired listener error:', err); }
      }
    }
  } else if (res.ok) {
    sessionExpiredFired = false;
  }

  if (!res.ok) {
    let errMsg = `${res.status} ${res.statusText}`;
    try {
      const data = await res.json();
      if (data?.error) errMsg = data.error;
    } catch {}
    throw new ApiError(errMsg, res.status);
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
};

// --- WebSocket manager ---

let socket = null;
let reconnectTimer = null;
let clientPingTimer = null;
let livenessTimer = null;
let lastServerActivityAt = 0;
let reconnectAttempts = 0;
const handlers = new Map();
const reconnectListeners = new Set();
const connectionStateListeners = new Set();
// 'open' | 'closed' | 'connecting'. Starts as 'closed'. Flips to
// 'connecting' the moment connectWebSocket() is called for the first
// time and to 'open' once auth completes. The UI uses this to decide
// when to show the "Reconnecting…" overlay (anything other than
// 'open', after we've connected at least once).
let connectionState = 'closed';
let hasEverConnected = false;

function setConnectionState(next) {
  if (connectionState === next) return;
  connectionState = next;
  if (next === 'open') hasEverConnected = true;
  for (const cb of connectionStateListeners) {
    try { cb(next, { hasEverConnected }); } catch (err) { console.error('[serverClient] connection state listener error:', err); }
  }
}
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];
// If we don't hear anything from the server for this long, the TCP link
// is half-open (NAT rebind, interface flip). Force-close so our reconnect
// loop takes over instead of sitting on a dead socket indefinitely.
const STALE_SERVER_MS = 45000;
const LIVENESS_CHECK_MS = 5000;

function getReconnectDelay() {
  const idx = Math.min(reconnectAttempts, RECONNECT_DELAYS.length - 1);
  return RECONNECT_DELAYS[idx];
}

// `await connectWebSocket(); send(...)` is the universal calling pattern
// (instance subscribes, room joins, presence). An async connect that
// returns at CONNECTING turns every one of those sends into a silently
// dropped message ("cannot send. Socket not open"), and a subscribe
// dropped on the FIRST connect is never replayed. The reconnect-replay
// in instancesApi only fires on re-opens. So connectWebSocket must not
// resolve until the socket is genuinely OPEN. The gate below hands every
// caller a promise that resolves true on the next successful open,
// staying pending across failed attempts in between (close → reconnect
// loop → eventual open releases everyone). The timeout bounds the wait
// when the server is down so callers fall through to their own failure
// paths (joinRoom's 10s "Join timeout", instance-sub replay on the
// eventual reconnect) instead of hanging forever.
const OPEN_WAIT_TIMEOUT_MS = 15000;
const openWaiters = new Set();

function whenSocketOpen() {
  if (socket?.readyState === 1) return Promise.resolve(true);
  return new Promise((resolve) => {
    const waiter = { resolve, timer: null };
    waiter.timer = setTimeout(() => {
      openWaiters.delete(waiter);
      resolve(false);
    }, OPEN_WAIT_TIMEOUT_MS);
    openWaiters.add(waiter);
  });
}

function releaseOpenWaiters(opened) {
  for (const waiter of openWaiters) {
    clearTimeout(waiter.timer);
    waiter.resolve(opened);
  }
  openWaiters.clear();
}

// Single-flight guard for the async gap between connectWebSocket's
// entry and `new WebSocket`. The awaits in between (getStoredToken,
// ensureClientVersion) yield to the event loop, so parallel callers
// (e.g. apiSubscribeInstance calls fanned out by _ensureInstanceSubs)
// would each reach `new WebSocket`. The server's addConnection then
// terminates the older socket, historically producing a 1s reconnect
// loop that never converged. A readyState check alone can't cover the
// gap because no socket exists yet.
let connectStarting = false;

// Resolves true once the socket is OPEN (immediately if it already is),
// false if no connection is possible (no token, forced-update rejection,
// explicit disconnect) or the open didn't happen within the bounded wait.
export async function connectWebSocket() {
  if (versionRejected) return false; // forced-update modal is showing; don't reconnect
  if (sessionSuperseded) return false; // account is live elsewhere; reclaimSession() is the way back
  if (connectStarting || (socket && socket.readyState <= 1)) return whenSocketOpen();

  connectStarting = true;
  try {
    const token = await getStoredToken();
    if (!token) {
      // No session. No connection is coming. Anything still waiting
      // (e.g. a reconnect attempt that straddled a logout) must not sit
      // out its full timeout.
      releaseOpenWaiters(false);
      return false;
    }

    // clientVersion is appended to the WS URL so the server upgrade
    // handler can gate compat before the connection is established.
    const clientVersion = await ensureClientVersion();
    const versionParam = clientVersion ? `&clientVersion=${encodeURIComponent(clientVersion)}` : '';
    // Platform marker for server-side insight tracking (browser vs
    // installed client). Old servers ignore the extra param.
    const platformParam = `&clientPlatform=${isWeb() ? 'web' : 'desktop'}`;
    // Tell the server this build treats close 4002 as terminal (the
    // latch below). The server only supersedes sockets that advertise
    // this: a client without the latch reads 4002 as a blip, reconnects
    // a second later, and that reconnect kicks whoever took over, which
    // is the qa#124 loop. It is a capability flag rather than a version
    // check because the server's semver compare strips pre-release
    // suffixes, making 0.29.4-rc.1 (no latch) and 0.29.4-rc.2 (latch)
    // indistinguishable.
    const supersedeParam = '&supersede=1';

    socket = new WebSocket(`${getServerWsUrl()}?token=${encodeURIComponent(token)}${versionParam}${platformParam}${supersedeParam}`);
  } finally {
    connectStarting = false;
  }
  const s = socket;
  setConnectionState('connecting');

  s.addEventListener('open', () => {
    if (socket !== s) return;
    const wasReconnect = reconnectAttempts > 0;
    reconnectAttempts = 0;
    lastServerActivityAt = Date.now();
    setConnectionState('open');
    releaseOpenWaiters(true);
    console.log('[serverClient] WebSocket connected');
    // Application-level keepalive: send a lightweight ping every 20s and
    // expect a pong back. Combined with the liveness timer below this
    // gives us end-to-end proof the connection is alive; without it, a
    // half-open TCP would leave us sending pings into the void while the
    // browser happily reports readyState=1.
    clearInterval(clientPingTimer);
    clearInterval(livenessTimer);
    clientPingTimer = setInterval(() => {
      if (s.readyState === 1) s.send(JSON.stringify({ type: 'ping' }));
    }, 20000);
    livenessTimer = setInterval(() => {
      if (s.readyState !== 1) return;
      if (Date.now() - lastServerActivityAt > STALE_SERVER_MS) {
        console.warn(`[serverClient] No server activity in ${STALE_SERVER_MS}ms. Forcing reconnect`);
        try { s.close(); } catch {}
      }
    }, LIVENESS_CHECK_MS);
    if (wasReconnect) {
      for (const cb of reconnectListeners) {
        try { cb(); } catch (err) { console.error('[serverClient] reconnect listener error:', err); }
      }
    }
  });

  s.addEventListener('message', (event) => {
    if (socket !== s) return;
    lastServerActivityAt = Date.now();
    try {
      const message = JSON.parse(event.data);
      if (!message?.type) return;
      // Swallow pong; it exists purely to refresh lastServerActivityAt above.
      if (message.type === 'pong') return;
      // Server says our client is too old. Flip the gate so the close
      // handler below doesn't queue a reconnect, and surface the payload
      // so the forced-update modal renders.
      if (message.type === 'version:rejected') {
        versionRejected = true;
        reportWebSocketRejection(message.data || {});
        return;
      }
      // Server retired this socket because the account connected from
      // another device or tab. Latch before the close arrives so the
      // close handler below skips the reconnect.
      if (message.type === 'session:superseded') {
        markSuperseded(message.data || {});
        return;
      }
      const list = handlers.get(message.type);
      if (list) {
        for (const cb of list) {
          try { cb(message.data, message); } catch (err) { console.error(err); }
        }
      }
    } catch (err) {
      console.error('[serverClient] Failed to parse message:', err);
    }
  });

  s.addEventListener('close', (event) => {
    // Only act on the close of the live socket. A stale socket that
    // lost the race to `socket` must NOT null the live reference or
    // schedule a reconnect. That path was the trigger for an endless
    // 1s reconnect loop when racing callers each created their own ws.
    if (socket !== s) return;
    console.log('[serverClient] WebSocket closed', event?.code);
    clearInterval(clientPingTimer);
    clearInterval(livenessTimer);
    clientPingTimer = null;
    livenessTimer = null;
    socket = null;
    setConnectionState('closed');
    // 4001 = client_too_old. The version:rejected message above usually
    // sets the flag first; setting it here too covers the case where the
    // message dropped on the wire.
    if (event?.code === 4001) versionRejected = true;
    // 4002 = session_superseded. Same belt-and-braces: the
    // session:superseded message normally lands first.
    if (event?.code === 4002) markSuperseded();
    if (versionRejected || sessionSuperseded) {
      releaseOpenWaiters(false);
      return;
    }
    // Waiters stay pending across the retry. The reconnect's open (or
    // their own timeout) releases them.
    scheduleReconnect();
  });

  s.addEventListener('error', (err) => {
    console.error('[serverClient] WebSocket error:', err);
  });

  return whenSocketOpen();
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  const delay = getReconnectDelay();
  reconnectAttempts++;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectWebSocket().catch((err) => console.error('[serverClient] reconnect failed:', err));
  }, delay);
}

export function disconnectWebSocket() {
  // An explicit local disconnect (logout / presence teardown) is the
  // player acting HERE, so the elsewhere-latch no longer applies. Left
  // set, it would silently block the next login's connect.
  sessionSuperseded = false;
  supersededInfo = null;
  clearInterval(clientPingTimer);
  clearInterval(livenessTimer);
  clientPingTimer = null;
  livenessTimer = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempts = 0;
  if (socket) {
    socket.close();
    socket = null;
  }
  releaseOpenWaiters(false);
}

// Subscribe to successful reconnects (NOT the initial connect). Fires
// after the socket has re-opened following any drop. Used by the
// GameBoard to re-request authoritative state when the link recovers
// mid-match, since the server's session:active path is a no-op when the
// match is already open on screen.
export function onServerReconnect(callback) {
  reconnectListeners.add(callback);
  return () => reconnectListeners.delete(callback);
}

// Subscribe to connection state transitions ('open' | 'connecting' |
// 'closed'). Fires immediately with the current state on subscribe so
// the listener doesn't need to poll for the initial value.
export function onConnectionState(callback) {
  connectionStateListeners.add(callback);
  try { callback(connectionState, { hasEverConnected }); } catch (err) {
    console.error('[serverClient] connection state listener error:', err);
  }
  return () => connectionStateListeners.delete(callback);
}

export function getConnectionState() {
  return { state: connectionState, hasEverConnected };
}

export function send(type, data) {
  if (!socket || socket.readyState !== 1) {
    console.warn(`[serverClient] cannot send. Socket not open (dropped ${type})`);
    return false;
  }
  socket.send(JSON.stringify({ type, data }));
  return true;
}

export function on(type, callback) {
  if (!handlers.has(type)) handlers.set(type, new Set());
  handlers.get(type).add(callback);
  return () => off(type, callback);
}

export function off(type, callback) {
  const set = handlers.get(type);
  if (set) set.delete(callback);
}

export function isConnected() {
  return socket?.readyState === 1;
}
